import { useEffect, useMemo, useState } from 'react';
import { Globe, X, Copy, Check, ExternalLink, Loader2 } from 'lucide-react';
import { usePanelStore } from '../panel/panel-store';
import { useCanvasStore } from '../../store/canvas-store';
import { useCurrentProject } from '../../projects/currentProject';
import { projectsApi } from '../../projects/useProjects';
import { publishSelection, publishableFrameIds } from '../../publish/publishSelection';

type Phase =
  | { t: 'idle' }
  | { t: 'publishing' }
  | { t: 'done'; url: string; at: number }
  | { t: 'error'; message: string };

/**
 * Publish modal (Wave 3, Task 3.4). Assembles the selected design frames into a
 * static site and uploads it to the active project's publish endpoint, then
 * surfaces the public URL with copy + open. Editors only.
 */
export function PublishModal() {
  const open = usePanelStore((s) => s.publishOpen);
  const setOpen = usePanelStore((s) => s.setPublishOpen);

  const selection = useCanvasStore((s) => s.selection);
  const doc = useCanvasStore((s) => s.doc);
  const projectId = useCurrentProject((s) => s.projectId);
  const projectTitle = useCurrentProject((s) => s.title);
  const role = useCurrentProject((s) => s.role);

  const publishable = useMemo(() => publishableFrameIds(selection, doc), [selection, doc]);
  const canPublish = role === 'owner' || role === 'editor';

  const [slug, setSlug] = useState('');
  const [phase, setPhase] = useState<Phase>({ t: 'idle' });
  const [copied, setCopied] = useState(false);

  // Reset transient state each time the modal opens; seed the slug from the title.
  useEffect(() => {
    if (!open) return;
    setSlug(projectTitle ?? '');
    setPhase({ t: 'idle' });
    setCopied(false);
  }, [open, projectTitle]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;

  const count = publishable.length;
  const ready = canPublish && count > 0 && !!projectId && phase.t !== 'publishing';

  const run = async () => {
    setPhase({ t: 'publishing' });
    try {
      const res = await publishSelection(
        { projectId, doc, selection, publish: (id, req) => projectsApi.publish(id, req) },
        slug || undefined,
      );
      setPhase({ t: 'done', url: res.url, at: res.publishedAt });
    } catch (err) {
      setPhase({ t: 'error', message: err instanceof Error ? err.message : 'Publish failed' });
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is still selectable */
    }
  };

  const inputCls =
    'w-full rounded-[8px] border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1.5 ' +
    'text-[13px] text-[var(--color-text)] outline-none focus-visible:border-[var(--color-accent)]';

  return (
    <div
      className="fixed inset-0 z-[200] grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Publish"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        data-anim
        className="cf-pop-in w-full max-w-[460px] rounded-[14px] border border-[var(--color-menu-border)] bg-[var(--color-surface)] p-5 text-[var(--color-text)] shadow-[var(--shadow-2)]"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            <Globe size={16} className="text-[var(--color-accent-2)]" />
            Publish
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="grid h-8 w-8 place-items-center rounded-[8px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)]"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        {!canPublish ? (
          <p className="text-[13px] text-[var(--color-text-muted)]">
            You need editor access to publish this project.
          </p>
        ) : count === 0 ? (
          <p className="text-[13px] text-[var(--color-text-muted)]">
            Select one or more design frames on the canvas, then publish.
          </p>
        ) : (
          <>
            <p className="mb-3 text-[13px] text-[var(--color-text-muted)]">
              Publishing {count === 1 ? '1 frame' : `${count} frames`} as a live web page.
            </p>

            <label className="mb-1 block text-[12px] text-[var(--color-text-muted)]">URL slug</label>
            <input
              className={inputCls}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-landing-page"
              spellCheck={false}
              disabled={phase.t === 'publishing'}
            />

            {phase.t === 'error' && (
              <p className="mt-3 text-[12px] text-[var(--color-danger)]">{phase.message}</p>
            )}

            {phase.t === 'done' && (
              <div className="mt-4 rounded-[10px] border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
                <div className="mb-2 flex items-center gap-1.5 text-[12px] text-[var(--color-accent-2)]">
                  <Check size={14} /> Published
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={phase.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 truncate text-[13px] text-[var(--color-accent)] underline"
                  >
                    {phase.url}
                  </a>
                  <button
                    type="button"
                    onClick={() => copy(phase.url)}
                    className="grid h-7 w-7 place-items-center rounded-[6px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)]"
                    title="Copy link"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <a
                    href={phase.url}
                    target="_blank"
                    rel="noreferrer"
                    className="grid h-7 w-7 place-items-center rounded-[6px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)]"
                    title="Open in new tab"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-[8px] border border-[var(--color-border)] px-3 py-1.5 text-[13px] text-[var(--color-text)] hover:bg-[var(--color-menu-hover)]"
              >
                {phase.t === 'done' ? 'Done' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={run}
                disabled={!ready}
                className={
                  'flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-[13px] font-medium text-white ' +
                  'bg-[var(--color-accent)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                }
              >
                {phase.t === 'publishing' && <Loader2 size={14} className="animate-spin" />}
                {phase.t === 'done' ? 'Re-publish' : 'Publish'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
